import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Package, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  confirmed: "bg-emerald-100 text-emerald-700",
  shipped: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const RETURN_STATUS = {
  requested: { label: "Return requested", c: "bg-amber-100 text-amber-700" },
  approved: { label: "Return approved", c: "bg-blue-100 text-blue-700" },
  picked_up: { label: "Picked up", c: "bg-indigo-100 text-indigo-700" },
  refunded: { label: "Refunded", c: "bg-emerald-100 text-emerald-700" },
  completed: { label: "Completed", c: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Request rejected", c: "bg-red-100 text-red-700" },
};

export default function Orders() {
  const { user, formatPrice, setAuthModalOpen } = useApp();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [dialog, setDialog] = useState(null); // {order, index, item}
  const [form, setForm] = useState({ type: "return", reason: "", refund_method: "wallet", exchange_size: "", exchange_color: "", pickup_date: "" });
  const [saving, setSaving] = useState(false);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const load = () => {
    api.get("/orders").then(({ data }) => setOrders(data));
    api.get("/returns").then(({ data }) => setReturns(data)).catch(() => {});
  };
  useEffect(() => { if (user) load(); }, [user]);

  const returnFor = (orderId, idx) =>
    returns.find((r) => r.order_id === orderId && r.item_index === idx && r.status !== "rejected");

  const openReturn = (order, index, item) => {
    setForm({ type: "return", reason: "", refund_method: "wallet", exchange_size: item.size || "", exchange_color: item.color || "", pickup_date: tomorrow });
    setDialog({ order, index, item });
  };

  const submitReturn = async () => {
    if (!form.reason.trim()) { toast.error("Please tell us the reason"); return; }
    setSaving(true);
    try {
      await api.post("/returns", {
        order_id: dialog.order.id,
        item_index: dialog.index,
        type: form.type,
        reason: form.reason,
        refund_method: form.type === "return" ? form.refund_method : null,
        exchange_size: form.type === "exchange" ? form.exchange_size : null,
        exchange_color: form.type === "exchange" ? form.exchange_color : null,
        pickup_date: form.pickup_date || null,
      });
      toast.success("Request submitted — we'll review it shortly");
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit request");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <Package size={56} className="mx-auto text-slate-300 mb-4" />
        <h1 className="font-display text-2xl font-bold mb-3">My Orders</h1>
        <Button onClick={() => setAuthModalOpen(true)} className="bg-[#E63946] hover:bg-[#d12836]">Login</Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold">My Orders</h1>
        <Link to="/returns" className="text-sm font-semibold text-[#E63946] hover:underline">Returns policy</Link>
      </div>
      {orders.length === 0 ? (
        <div className="text-center py-20">
          <Package size={56} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 mb-6">No orders yet.</p>
          <Link to="/shop"><Button className="bg-[#E63946] hover:bg-[#d12836]">Start Shopping</Button></Link>
        </div>
      ) : (
        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => {
            const eligible = o.payment_status === "paid" && o.status !== "cancelled";
            return (
              <div key={o.id} className="p-5 rounded-2xl border bg-white dark:bg-slate-900" data-testid="order-item">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-xs text-slate-400">Order #{o.id.slice(-8).toUpperCase()}</div>
                    <div className="text-xs text-slate-400">{new Date(o.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[o.status] || "bg-slate-100 text-slate-600"}`}>{o.status}</span>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${o.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status}</span>
                  </div>
                </div>
                <div className="space-y-2">
                  {o.items.map((it, i) => {
                    const req = returnFor(o.id, i);
                    return (
                      <div key={i} className="flex gap-3 items-center bg-slate-50 dark:bg-slate-800 rounded-lg p-2 pr-3">
                        <img src={it.image} alt="" className="w-11 h-14 object-cover rounded" />
                        <div className="text-xs flex-1 min-w-0">
                          <div className="font-medium line-clamp-1">{it.name}</div>
                          <div className="text-slate-400">Qty {it.quantity}{it.size ? ` • ${it.size}` : ""}{it.logo === false ? " • No logo" : ""}</div>
                        </div>
                        {req ? (
                          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${RETURN_STATUS[req.status]?.c}`} data-testid={`return-status-${o.id}-${i}`}>
                            {req.type === "exchange" ? "Exchange" : "Return"}: {RETURN_STATUS[req.status]?.label || req.status}
                          </span>
                        ) : eligible ? (
                          <Button size="sm" variant="outline" className="shrink-0 text-xs h-8" onClick={() => openReturn(o, i, it)} data-testid={`return-btn-${o.id}-${i}`}>
                            <RotateCcw size={13} className="mr-1" /> Return / Exchange
                          </Button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t">
                  <span className="text-sm text-slate-500">Total ({o.currency})</span>
                  <span className="font-mono-price font-bold">
                    {o.currency === "INR" ? `₹${Math.round(o.amount)}` : `${o.currency} ${o.amount.toFixed(2)}`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* return/exchange dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="return-dialog">
          <DialogHeader><DialogTitle>Return or Exchange</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="flex gap-3 items-center bg-slate-50 dark:bg-slate-800 rounded-lg p-2">
                <img src={dialog.item.image} alt="" className="w-11 h-14 object-cover rounded" />
                <div className="text-sm"><div className="font-medium">{dialog.item.name}</div><div className="text-xs text-slate-400">Qty {dialog.item.quantity}{dialog.item.size ? ` • ${dialog.item.size}` : ""}</div></div>
              </div>
              <div>
                <label className="text-sm font-semibold mb-1.5 block">What would you like?</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="return-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="return">Return for a refund</SelectItem>
                    <SelectItem value="exchange">Exchange (size / colour)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.type === "return" ? (
                <div>
                  <label className="text-sm font-semibold mb-1.5 block">Refund to</label>
                  <Select value={form.refund_method} onValueChange={(v) => setForm({ ...form, refund_method: v })}>
                    <SelectTrigger data-testid="return-refund-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wallet">Bull Family Wallet (instant)</SelectItem>
                      <SelectItem value="original">Original payment method (5–7 days)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-sm font-semibold mb-1.5 block">New size</label><Input value={form.exchange_size} onChange={(e) => setForm({ ...form, exchange_size: e.target.value })} data-testid="exchange-size" /></div>
                  <div><label className="text-sm font-semibold mb-1.5 block">New colour</label><Input value={form.exchange_color} onChange={(e) => setForm({ ...form, exchange_color: e.target.value })} data-testid="exchange-color" /></div>
                </div>
              )}
              <div>
                <label className="text-sm font-semibold mb-1.5 block">Reason</label>
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g. Size too small / changed my mind" data-testid="return-reason" />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1.5 block">Preferred pickup date</label>
                <Input type="date" min={tomorrow} value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} data-testid="return-pickup-date" />
              </div>
              <p className="text-xs text-slate-400">Free doorstep pickup will be arranged on your preferred date after we review your request.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={submitReturn} disabled={saving} className="bg-[#E63946] hover:bg-[#d12836]" data-testid="return-submit">
              {saving ? "Submitting..." : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
